import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { MessageCircle, X, Send, Loader2, Bug, HelpCircle, Sparkles, MousePointer2, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

const SUPPORT_EMAIL = "hello@sisyphean.ai";
const DISCORD_INVITE_URL = "https://discord.gg/tDwaSvK4S";

/**
 * Floating site-wide tools, both anchored bottom-LEFT (stacked):
 *   • Help bot (top of stack) - chat + explain mode.
 *   • Feedback bubble (below) - bug/suggestion/feedback intake.
 * Plus a globally-listening Contact popup (`window` event `sisyphean:open-contact`)
 * that nudges users toward the bot before opening their email client.
 *
 * Wrapped in a single <div> so the global `#root > * { position: relative }` rule
 * doesn't clobber the launchers' `position: fixed`.
 *
 * Feedback bubble auto-hides on /admin (admins read feedback there, don't submit it).
 */
export default function FloatingTools() {
  const location = useLocation();
  const { user } = useAuth();
  const onAdmin = location.pathname.startsWith("/admin");
  const [helpOpen, setHelpOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [discordOpen, setDiscordOpen] = useState(false);
  const canAccessDiscord = !!(user?.is_paid);

  // Listen for "Support" links anywhere in the app.
  useEffect(() => {
    const handler = () => setContactOpen(true);
    window.addEventListener("sisyphean:open-contact", handler);
    return () => window.removeEventListener("sisyphean:open-contact", handler);
  }, []);

  return (
    <div data-testid="floating-tools-root">
      {/* Stack (bottom-LEFT): discord (top) -> help bot -> feedback (bottom). */}
      <FloatingLauncher
        testid="discord-launcher"
        ariaLabel="Join the Sisyphean Discord"
        tooltip="Join our Discord"
        icon={<DiscordIcon />}
        onClick={() => setDiscordOpen(true)}
        style={{ bottom: "132px", left: "20px" }}
      />
      {!helpOpen && (
        <FloatingLauncher
          testid="help-bot-launcher"
          ariaLabel="Open help chat"
          tooltip="Need help?"
          icon={<MessageCircle className="w-4 h-4" strokeWidth={1.75} />}
          onClick={() => setHelpOpen(true)}
          style={{ bottom: "76px", left: "20px" }}
        />
      )}
      {!onAdmin && (
        <FeedbackButton pagePath={location.pathname} style={{ bottom: "20px", left: "20px" }} />
      )}

      {helpOpen && <HelpPanel pagePath={location.pathname} onClose={() => setHelpOpen(false)} />}
      {contactOpen && (
        <ContactPopup
          onClose={() => setContactOpen(false)}
          onOpenBot={() => { setContactOpen(false); setHelpOpen(true); }}
        />
      )}
      {discordOpen && <DiscordPopup canAccess={canAccessDiscord} onClose={() => setDiscordOpen(false)} />}
    </div>
  );
}

function DiscordIcon() {
  // Inline Discord glyph - matches the size/stroke of lucide icons used in
  // the other launchers without pulling in a new icon package.
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="w-4 h-4"
    >
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.16.286-.346.674-.472.98a18.36 18.36 0 0 0-5.176 0c-.126-.306-.317-.694-.477-.98A19.736 19.736 0 0 0 6.673 4.37C2.99 9.84 1.91 15.18 2.443 20.45a19.91 19.91 0 0 0 5.94 2.99c.482-.66.913-1.36 1.282-2.1-.706-.265-1.38-.588-2.018-.962.17-.123.336-.252.498-.384 3.946 1.808 8.21 1.808 12.106 0 .164.132.33.261.5.384-.64.374-1.314.7-2.02.964.37.74.8 1.44 1.283 2.1a19.86 19.86 0 0 0 5.94-2.99c.624-6.04-1.057-11.337-4.637-16.083ZM9.683 16.156c-1.184 0-2.156-1.082-2.156-2.41 0-1.328.95-2.41 2.156-2.41 1.207 0 2.18 1.094 2.157 2.41 0 1.328-.95 2.41-2.157 2.41Zm7.974 0c-1.184 0-2.156-1.082-2.156-2.41 0-1.328.95-2.41 2.156-2.41 1.206 0 2.18 1.094 2.157 2.41 0 1.328-.95 2.41-2.157 2.41Z" />
    </svg>
  );
}

function DiscordPopup({ canAccess, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const open = () => {
    window.open(DISCORD_INVITE_URL, "_blank", "noopener,noreferrer");
    onClose();
  };
  return (
    <div
      data-testid="discord-popup"
      className="fixed inset-0 z-[140] flex items-center justify-center px-4 bg-background/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-sm p-6">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
        <div className="terminal-label text-primary mb-3">[ COMMUNITY ]</div>
        <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-3">Sisyphean Discord</h3>
        {canAccess ? (
          <>
            <p className="text-sm text-foreground/80 leading-relaxed mb-6">
              Talk shop with other paying Sisyphean members. Share wins, swap feedback on case studies, and get a direct line to me while we&apos;re still building.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="btn-ghost text-[11px]" data-testid="discord-cancel">Maybe later</button>
              <button onClick={open} className="btn-primary text-[11px] inline-flex items-center gap-2" data-testid="discord-open">
                <DiscordIcon /> Open Discord
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-foreground/25 text-[9px] uppercase tracking-[0.22em] font-mono text-foreground/65 mb-4">
              <Lock className="w-2.5 h-2.5" /> Members only
            </div>
            <p className="text-sm text-foreground/80 leading-relaxed mb-3">
              The Discord is reserved for paying members - founders and monthly subscribers. It&apos;s a small room and we want the signal-to-noise high.
            </p>
            <p className="text-[12px] text-foreground/65 leading-relaxed mb-6">
              Grab a founder seat (lifetime, $200) and you&apos;re in.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="btn-ghost text-[11px]" data-testid="discord-cancel">Not yet</button>
              <Link to="/subscription" onClick={onClose} className="btn-primary text-[11px] inline-flex items-center gap-2" data-testid="discord-upgrade">
                See plans
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable launcher: small circular button with a tooltip-on-hover label.    */
/* -------------------------------------------------------------------------- */
function FloatingLauncher({ testid, ariaLabel, tooltip, icon, onClick, style, variant = "default" }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="fixed z-[100]"
      style={style}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        data-testid={testid}
        onClick={onClick}
        aria-label={ariaLabel}
        title={tooltip}
        className={`h-10 w-10 rounded-full border flex items-center justify-center shadow-lg transition-colors ${
          variant === "primary"
            ? "bg-foreground text-background border-foreground hover:bg-primary hover:border-primary"
            : "bg-surface border-foreground/30 text-foreground hover:bg-foreground hover:text-background"
        }`}
      >
        {icon}
      </button>
      {/* Hover tooltip - only on cursor hover. */}
      <div
        data-testid={`${testid}-tooltip`}
        className={`absolute left-12 top-1/2 -translate-y-1/2 whitespace-nowrap px-2.5 py-1.5 bg-foreground text-background text-[10px] uppercase tracking-[0.22em] font-mono shadow-lg pointer-events-none transition-opacity duration-150 ${
          hover ? "opacity-100" : "opacity-0"
        }`}
      >
        {tooltip}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Help bot                                                                   */
/* -------------------------------------------------------------------------- */
function HelpPanel({ pagePath, onClose }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: `Hi - I'm the in-app guide. Ask me anything about Sisyphean and I'll answer right here.\n\nPrefer email? You can also reach a human at ${SUPPORT_EMAIL} - replies are slower than the bot, but I'll always be here in the meantime.\n\nTip: tap the cursor icon above to turn on explain mode and hover over anything you don't recognize.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [explainMode, setExplainMode] = useState(false);
  const scrollerRef = useRef(null);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { if (explainMode) setExplainMode(false); else onClose(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [explainMode, onClose]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const res = await api.post("/help/chat", { session_id: sessionId, message: msg, page_path: pagePath });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "bot", text: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "bot", text: `Sorry - I couldn't reach the help service. Try again in a moment, or email ${SUPPORT_EMAIL}.` }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div
        data-testid="help-bot-panel"
        className="fixed bottom-5 left-5 z-[100] w-[min(94vw,420px)] h-[min(75vh,640px)] bg-background border border-border shadow-2xl flex flex-col"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-foreground text-background">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-mono opacity-70">[ HELP.BOT ]</div>
            <div className="text-sm font-display uppercase tracking-tight font-medium">Sisyphean guide</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="help-bot-explain-toggle"
              onClick={() => setExplainMode((v) => !v)}
              title="Toggle explain mode - hover over anything"
              className={`p-1.5 rounded transition-colors ${explainMode ? "bg-primary text-primary-foreground" : "hover:bg-background/15"}`}
            >
              <MousePointer2 className="w-4 h-4" strokeWidth={2} />
            </button>
            <button data-testid="help-bot-close" onClick={onClose} aria-label="Close" className="p-1.5 hover:bg-background/15 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {explainMode && (
          <div className="px-4 py-2 bg-primary/10 border-b border-primary/30 text-[11px] font-mono uppercase tracking-[0.18em] text-primary flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Explain mode · click any element on the page · esc to exit
          </div>
        )}

        <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="help-bot-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-foreground text-background rounded-tr-none rounded-2xl"
                  : "bg-surface text-foreground rounded-tl-none rounded-2xl whitespace-pre-line"
              }`}>{m.text}</div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-surface px-3 py-2 rounded-2xl rounded-tl-none flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Thinking…</span>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border p-3 flex gap-2">
          <input
            data-testid="help-bot-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            disabled={busy}
            placeholder="Ask anything about Sisyphean…"
            className="input-base flex-1 text-sm"
          />
          <button
            data-testid="help-bot-send"
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="btn-primary px-3 disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {explainMode && <ExplainOverlay pagePath={pagePath} onResult={(text) => setMessages((m) => [...m, { role: "bot", text }])} onExit={() => setExplainMode(false)} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Explain mode overlay                                                       */
/* -------------------------------------------------------------------------- */
function ExplainOverlay({ pagePath, onResult, onExit }) {
  const [hovered, setHovered] = useState(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    let lastEl = null;
    const onMove = (e) => {
      const stack = document.elementsFromPoint(e.clientX, e.clientY);
      const target = stack.find((el) => !el.closest('[data-testid^="help-bot"]'));
      if (!target || target === lastEl) return;
      lastEl = target;
      const r = target.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setHovered({ rect: r, el: target });
    };
    const onClick = async (e) => {
      e.preventDefault(); e.stopPropagation();
      const stack = document.elementsFromPoint(e.clientX, e.clientY);
      const target = stack.find((el) => !el.closest('[data-testid^="help-bot"]'));
      if (!target) return;
      const text = (target.innerText || target.textContent || "").trim().slice(0, 400);
      const payload = {
        page_path: pagePath,
        element_text: text,
        element_role: target.getAttribute("role") || target.tagName.toLowerCase(),
        element_testid: target.getAttribute("data-testid") || "",
        element_aria_label: target.getAttribute("aria-label") || "",
        surrounding_text: (target.parentElement?.innerText || "").trim().slice(0, 500),
      };
      onExit();
      try {
        const res = await api.post("/help/explain", payload);
        onResult(`📍 "${(payload.element_text || payload.element_testid || "this element").slice(0, 80)}":\n\n${res.data.explanation}`);
      } catch {
        onResult("Couldn't explain that one - try again in a moment.");
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("click", onClick, true);
    };
  }, [pagePath, onResult, onExit]);

  return (
    <div ref={overlayRef} className="fixed inset-0 z-[99] pointer-events-none" data-testid="help-explain-overlay">
      {hovered && (
        <div
          className="absolute border-2 border-primary bg-primary/10 transition-all duration-100"
          style={{
            left: hovered.rect.left - 2,
            top: hovered.rect.top - 2,
            width: hovered.rect.width + 4,
            height: hovered.rect.height + 4,
          }}
        />
      )}
      <button
        data-testid="explain-exit-fab"
        onClick={onExit}
        className="pointer-events-auto fixed top-5 left-1/2 -translate-x-1/2 z-[101] bg-foreground text-background px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-mono flex items-center gap-2 shadow-lg"
      >
        <X className="w-3 h-3" /> Exit explain mode · esc
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Feedback                                                                   */
/* -------------------------------------------------------------------------- */
function FeedbackButton({ pagePath, style }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <FloatingLauncher
        testid="feedback-launcher"
        ariaLabel="Submit feedback or bug report"
        tooltip="Bug report / feedback"
        icon={<Bug className="w-4 h-4" strokeWidth={1.75} />}
        onClick={() => setOpen(true)}
        style={style}
      />
      {open && <FeedbackModal pagePath={pagePath} onClose={() => setOpen(false)} />}
    </>
  );
}

function FeedbackModal({ pagePath, onClose }) {
  const [type, setType] = useState("bug");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const submit = async () => {
    if (text.trim().length < 5) {
      toast.error("Tell us a bit more - even one sentence helps.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", { type, text: text.trim(), page_path: pagePath });
      toast.success("Sent - thank you. We read every one.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="feedback-modal"
      className="fixed inset-0 z-[120] flex items-start justify-center pt-16 px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-lg p-6 md:p-7">
        <button onClick={onClose} disabled={busy} aria-label="Close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground disabled:opacity-40">
          <X className="w-4 h-4" />
        </button>

        <div className="terminal-label text-primary mb-2">[ TELL.US ]</div>
        <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-1">Send feedback</h3>
        <p className="text-xs text-muted-foreground leading-relaxed mb-5">
          Bug? Idea? Annoyance? Drop it here - a human reads every submission. We prioritize automatically (critical bugs jump the queue).
        </p>

        <div className="flex gap-2 mb-4">
          {[
            { k: "bug",        label: "Bug",        icon: Bug },
            { k: "suggestion", label: "Suggestion", icon: Sparkles },
            { k: "feedback",   label: "Feedback",   icon: HelpCircle },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              type="button"
              data-testid={`feedback-type-${k}`}
              onClick={() => setType(k)}
              disabled={busy}
              className={`flex-1 px-2 sm:px-3 py-2 text-[11px] uppercase tracking-[0.12em] sm:tracking-[0.18em] font-mono border flex items-center justify-center gap-1.5 transition-colors ${
                type === k
                  ? "bg-foreground text-background border-foreground"
                  : "border-foreground/25 text-foreground/70 hover:border-foreground/55"
              }`}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>

        <textarea
          data-testid="feedback-text"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
          rows={6}
          maxLength={2000}
          placeholder={
            type === "bug" ? "What broke? Steps to reproduce help a lot."
            : type === "suggestion" ? "What would you like Sisyphean to do?"
            : "Tell us anything - what's working, what's not."
          }
          className="input-base w-full resize-y text-sm leading-relaxed"
        />
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono mt-1 mb-5">
          {text.length} / 2000 · page: {pagePath || "/"}
        </p>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="btn-ghost text-[11px]">Cancel</button>
          <button
            data-testid="feedback-submit"
            onClick={submit}
            disabled={busy || text.trim().length < 5}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : <>Send →</>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Contact popup - fired by any "Support" link via window event.              */
/* -------------------------------------------------------------------------- */
function ContactPopup({ onClose, onOpenBot }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="contact-popup"
      className="fixed inset-0 z-[130] flex items-center justify-center px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-md p-6 md:p-7">
        <button onClick={onClose} aria-label="Close" data-testid="contact-popup-close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>

        <div className="terminal-label text-primary mb-2">[ GET.HELP ]</div>
        <h3 className="font-display text-xl uppercase font-bold tracking-tight mb-2">Need a hand?</h3>
        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
          The in-app help bot answers most questions <span className="text-foreground font-medium">instantly</span> - it knows the product cold. You can also email a human at <span className="font-mono text-foreground">{SUPPORT_EMAIL}</span>, but replies will be slower.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="contact-popup-open-bot"
            onClick={onOpenBot}
            className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-3.5 h-3.5" /> Ask the bot
          </button>
          <a
            data-testid="contact-popup-continue-email"
            href={`mailto:${SUPPORT_EMAIL}`}
            onClick={onClose}
            className="btn-secondary flex-1 inline-flex items-center justify-center gap-2 no-underline"
          >
            <Mail className="w-3.5 h-3.5" /> Continue to email
          </a>
        </div>
      </div>
    </div>
  );
}
